import { localYmd } from "./ownerJarvisUtils";

const SNAPSHOT_INVALIDATION_EVENT = "webprofit:owner-operating-snapshot-invalidated";
const ALL_SCOPE = "*";

export const OWNER_JARVIS_SNAPSHOT_SCOPES = Object.freeze([
  "context",
  "today",
  "commitments",
  "projects",
  "people",
  "waiting",
  "calendar",
  "finance",
  "routines",
]);

const KNOWN_SCOPES = new Set(OWNER_JARVIS_SNAPSHOT_SCOPES);

function asNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function normalizeScopes(scopes, { allowAll = true } = {}) {
  if (scopes == null || scopes === ALL_SCOPE) return allowAll ? [ALL_SCOPE] : [];
  const values = Array.isArray(scopes) ? scopes : [scopes];
  const normalized = [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
  if (allowAll && normalized.includes(ALL_SCOPE)) return [ALL_SCOPE];
  return normalized.filter((scope) => KNOWN_SCOPES.has(scope));
}

function legacySnapshotFromState(state = {}) {
  const operating = state?.operating || {};
  const calendar = state?.calendar || {};
  const assistant = state?.assistant || {};
  const center = state?.center || {};
  const routines = (calendar?.day?.routines || []).filter(Boolean);
  const commitments = (center?.life?.commitments || []).filter(Boolean);
  const projects = (center?.life?.initiatives || []).filter(Boolean);
  const people = (center?.people?.focus || assistant?.clients || []).filter(Boolean);
  const direction = operating?.direction || {};

  return {
    schemaVersion: 0,
    reasonCodesVersion: Number(assistant?.reasonCodesVersion || 0),
    generatedAt: state?.generatedAt || new Date().toISOString(),
    context: {
      date: calendar?.selectedDate || localYmd(),
      month: calendar?.month || assistant?.month || "",
      currency: operating?.currency || assistant?.currency || "ARS",
      tzOffsetMinutes: new Date().getTimezoneOffset(),
    },
    today: {
      workMinutes: asNumber(operating?.today?.workMinutes),
      openCommitmentCount: asNumber(operating?.today?.openCommitmentCount),
      pendingRoutineCount: asNumber(operating?.today?.pendingRoutineCount),
      capacity: calendar?.day?.capacity || null,
      direction: {
        kind: direction.kind || "free",
        highlightSection: direction.highlightSection || "today-jarvis",
        label: direction.question?.text || direction.commitment?.title || direction.routine?.name || "",
        ref: null,
        reasons: Array.isArray(direction.reasons) ? direction.reasons : [],
      },
      overviewHighlightSection: operating?.overviewHighlightSection || "today-money",
    },
    commitments: {
      openCount: commitments.length,
      overdueCount: asNumber(center?.life?.overdueCommitmentCount),
      items: commitments.slice(0, 20),
    },
    projects: {
      activeCount: asNumber(assistant?.stats?.activeProjects || projects.length),
      pendingDailyReviewCount: asNumber(operating?.projects?.pendingDailyReviewCount),
      nextDailyReview: operating?.projects?.nextDailyReview || null,
      items: projects.slice(0, 20),
    },
    people: {
      focusCount: people.length,
      attentionCount: asNumber(center?.people?.attentionCount),
      items: people.slice(0, 12),
    },
    waiting: { count: 0, overdueCount: 0, items: [] },
    calendar: {
      selectedDate: calendar?.selectedDate || localYmd(),
      upcoming: (assistant?.upcomingEvents || []).slice(0, 16),
      week: calendar?.week || null,
    },
    finance: {
      currency: operating?.currency || assistant?.currency || "ARS",
      available: asNumber(operating?.money?.available),
      outstanding: asNumber(operating?.money?.outstanding),
      uncoveredGap: asNumber(assistant?.economy?.uncoveredGap),
      gapAfterQuotes: asNumber(assistant?.economy?.gapAfterQuotes),
      monthlyRecurringFloor: asNumber(assistant?.economy?.monthlyRecurringFloor),
      monthlyRecurringGap: asNumber(assistant?.economy?.monthlyRecurringGap),
      reasons: Array.isArray(assistant?.guidance?.reasons) ? assistant.guidance.reasons : [],
    },
    routines: {
      scheduledTodayCount: routines.length,
      pendingTodayCount: routines.filter((row) => !row.completed).length,
      completedTodayCount: routines.filter((row) => row.completed).length,
      items: routines,
    },
  };
}

export function invalidateOwnerJarvisSnapshotCache(scopes = ALL_SCOPE) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  const normalizedScopes = normalizeScopes(scopes);
  const detail = { scopes: normalizedScopes.length ? normalizedScopes : [ALL_SCOPE] };
  if (typeof CustomEvent === "function") {
    window.dispatchEvent(new CustomEvent(SNAPSHOT_INVALIDATION_EVENT, { detail }));
    return;
  }
  const event = new Event(SNAPSHOT_INVALIDATION_EVENT);
  event.detail = detail;
  window.dispatchEvent(event);
}

export function createOwnerJarvisSnapshotService({ ownerApi, ttlMs = 60000 } = {}) {
  let snapshot = null;
  let staleAll = false;
  const staleScopes = new Set();
  let listening = false;
  let refreshPromise = null;

  const clearAll = () => {
    snapshot = null;
    staleAll = false;
    staleScopes.clear();
  };

  const markStale = (scopes = ALL_SCOPE) => {
    const normalizedScopes = normalizeScopes(scopes);
    if (!normalizedScopes.length || normalizedScopes.includes(ALL_SCOPE)) {
      staleAll = true;
      staleScopes.clear();
      return;
    }
    normalizedScopes.forEach((scope) => staleScopes.add(scope));
  };

  const onInvalidation = (event) => {
    markStale(event?.detail?.scopes || ALL_SCOPE);
  };

  function start() {
    if (listening || typeof window === "undefined" || typeof window.addEventListener !== "function") return;
    window.addEventListener(SNAPSHOT_INVALIDATION_EVENT, onInvalidation);
    listening = true;
  }

  start();

  async function refresh() {
    if (refreshPromise) return refreshPromise;
    refreshPromise = ownerApi.state({ currency: "ARS", date: localYmd() })
      .then((response) => {
        const normalized = response?.state?.snapshot || legacySnapshotFromState(response?.state || {});
        snapshot = {
          ...normalized,
          capturedAt: Date.now(),
        };
        staleAll = false;
        staleScopes.clear();
        return snapshot;
      })
      .finally(() => {
        refreshPromise = null;
      });
    return refreshPromise;
  }

  function isFreshFor(scopes) {
    if (!snapshot || staleAll) return false;
    if (Date.now() - snapshot.capturedAt >= ttlMs) return false;
    const requested = normalizeScopes(scopes, { allowAll: false });
    if (!requested.length) return staleScopes.size === 0;
    return requested.every((scope) => !staleScopes.has(scope));
  }

  async function current({ force = false, scopes = ALL_SCOPE } = {}) {
    if (!force && isFreshFor(scopes)) return snapshot;
    return refresh();
  }

  function invalidate(scopes = ALL_SCOPE) {
    markStale(scopes);
    invalidateOwnerJarvisSnapshotCache(scopes);
  }

  function destroy() {
    if (listening && typeof window !== "undefined" && typeof window.removeEventListener === "function") {
      window.removeEventListener(SNAPSHOT_INVALIDATION_EVENT, onInvalidation);
    }
    listening = false;
    clearAll();
  }

  function diagnostics() {
    return {
      hasSnapshot: Boolean(snapshot),
      staleAll,
      staleScopes: [...staleScopes],
      capturedAt: snapshot?.capturedAt || 0,
    };
  }

  return { current, refresh, invalidate, start, destroy, diagnostics, ttlMs };
}
